import {
  BuildingIcon,
  CarIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UserIcon,
  XIcon,
} from "lucide-react"
import type { MouseEvent } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { usePermission } from "@/contexts/permission"
import { PermissionsRolesEnum } from "@/enums"
import { SubscriptionStatusEnum } from "@/enums/subscriptions"
import { formatMoney } from "@/helpers/format"
import type { PeriodicityEnum } from "@/helpers/get-periodicity"
import { getPeriodicity } from "@/helpers/get-periodicity"
import {
  useSubscriptionById,
  useSubscriptionsSearchParams,
} from "@/hooks"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"

import { Button, buttonVariants } from "../ui/button"
import { CancelSubscriptionDialog } from "./cancel-subscription-dialog"
import { CyclesTable } from "./cycles-table"
import { SubscriptionDetailsSkeleton } from "./details-skeleton"
import { SubscriptionsStatusBadge } from "./status-badge"
import { getInsuranceTypeLabel } from "./SubscriptionsHelper"

export const SubscriptionDetails = () => {
  const { t } = useTranslation()
  const { params } = useSubscriptionsSearchParams()
  const { data, isFetching, error } = useSubscriptionById(
    params?.code as string
  )
  const { hasPermission } = usePermission()
  const [
    showCancelSubscriptionDialog,
    setShowCancelSubscriptionDialog,
  ] = useState(false)

  const [activeTab, setActiveTab] = useState<string>(() => {
    const hash = window.location.hash.replace("#", "")
    return hash || "details"
  })

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    window.history.pushState(null, "", `#${value}`)
  }

  const recurringUnit = getPeriodicity(
    data?.recurringUnit as PeriodicityEnum
  )
  const amount = data?.amount || 0
  const discount = data?.discount || 0
  const interest = data?.interest || 0
  const totalAmount = data?.totalAmount
    ? formatMoney(data.totalAmount / 100)
    : "-"
  const isVTEX = data?.type === "VTEX"
  const startDate = DateHelper.formatDate(
    data?.startDate,
    "dd/MM/yyyy"
  )
  const hasInsurance = Boolean(data?.insurance)
  const hasCars = Boolean(
    data?.insurance?.policyInformation?.cars?.length
  )
  const hasPaymentMethods = Boolean(
    data?.paymentMethods?.length
  )
  const hasPaymentConfig = Boolean(
    data?.defaultPaymentConfiguration
  )
  const hasMetadata = Boolean(data?.metadata?.length)
  const canCancelSubscription = hasPermission([
    PermissionsRolesEnum.SUBSCRIPTIONS_WRITE,
  ])
  const isCancellable =
    ![
      SubscriptionStatusEnum.CANCELED,
      SubscriptionStatusEnum.EXPIRED,
      SubscriptionStatusEnum.FINISHED,
    ].includes(data?.status as SubscriptionStatusEnum) &&
    !isVTEX &&
    canCancelSubscription
  const handleCancelButtonOnClick = (event: MouseEvent) => {
    event.stopPropagation()
    setShowCancelSubscriptionDialog(true)
  }
  const dataTable = data?.cycles?.map((cycle) => ({
    ...cycle,
    subscriptionId: data?.id,
    subscriptionType: data?.type,
    code: data?.code,
  }))
  const hasData = Boolean(data)
  const title =
    data?.code ??
    error?.message ??
    t("subscriptions.details.notFound")

  if (isFetching) {
    return <SubscriptionDetailsSkeleton />
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-muted-foreground">
              {t("subscriptions.details.title")}
            </h2>
            {hasData && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1">
                  <h1 className="page-title">{title}</h1>
                  <ClipboardButton
                    text={data?.code || ""}
                  />
                </div>
                <SubscriptionsStatusBadge
                  status={
                    data?.status as SubscriptionStatusEnum
                  }
                />
              </div>
            )}
          </div>
          {hasData && isCancellable && (
            <Button
              className="gap-1"
              variant="danger"
              onClickCapture={handleCancelButtonOnClick}
            >
              <XIcon size={16} />
              {t("subscriptions.details.cancel")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasData && (
          <section className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              {error?.message ??
                t("subscriptions.details.loadError")}
            </p>
          </section>
        )}
        {hasData && (
          <>
            <section>
              {data?.status ===
                SubscriptionStatusEnum.CREATED && (
                <div className="rounded-md border-2 border-blue-500/30 bg-blue-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.created"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.UNDER_REVIEW && (
                <div className="rounded-md border-2 border-yellow-500/30 bg-yellow-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.underReview"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.REVIEW_APPROVED && (
                <div className="rounded-md border-2 border-green-500/30 bg-green-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.reviewApproved"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.REVIEW_REJECTED && (
                <div className="rounded-md border-2 border-red-500/30 bg-red-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.reviewRejected"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.REVIEW_EXPIRED && (
                <div className="rounded-md border-2 border-orange-500/30 bg-orange-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.reviewExpired"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.IN_PROGRESS && (
                <div className="rounded-md border-2 border-green-500/30 bg-green-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.inProgress"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.PAST_DUE && (
                <div className="rounded-md border-2 border-orange-500/30 bg-orange-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.pastDue"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.EXPIRED && (
                <div className="rounded-md border-2 border-amber-500/30 bg-amber-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.expired"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.CANCELED && (
                <div className="rounded-md border-2 border-destructive/30 bg-destructive/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.canceled"
                    )}
                  </p>
                </div>
              )}
              {data?.status ===
                SubscriptionStatusEnum.FINISHED && (
                <div className="rounded-md border-2 border-green-500/30 bg-green-500/20 p-4">
                  <p className="text-sm">
                    {t(
                      "subscriptions.details.statusMessages.finished"
                    )}
                  </p>
                </div>
              )}
            </section>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="w-full"
            >
              <div className="w-full overflow-x-auto">
                <TabsList className="mb-2 w-full justify-start sm:justify-center">
                  <TabsTrigger
                    value="details"
                    className="text-xs sm:text-sm"
                  >
                    {t(
                      "subscriptions.details.tabs.details"
                    )}
                  </TabsTrigger>
                  {hasInsurance && (
                    <TabsTrigger
                      value="insurance"
                      className="text-xs sm:text-sm"
                    >
                      {t(
                        "subscriptions.details.tabs.insurance"
                      )}
                    </TabsTrigger>
                  )}
                  <TabsTrigger
                    value="cycles"
                    className="text-xs sm:text-sm"
                  >
                    {t("subscriptions.details.tabs.cycles")}
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent
                value="details"
                className="space-y-6"
              >
                <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  <SectionCard
                    title={t(
                      "subscriptions.details.subscriptionInfo"
                    )}
                    icon={<RefreshCwIcon size={16} />}
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <InfoItem
                        label="ID"
                        value={data?.id}
                        copyable
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.code"
                        )}
                        value={data?.code}
                        copyable
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.totalCycles"
                        )}
                        value={data?.cycles?.length?.toString()}
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.recurringUnit"
                        )}
                        value={recurringUnit}
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.startDate"
                        )}
                        value={startDate}
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.expiresAt"
                        )}
                        value={DateHelper.formatDate(
                          data?.expiresAt,
                          "dd/MM/yyyy"
                        )}
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.createdAt"
                        )}
                        value={
                          data?.createdAt
                            ? DateHelper.format(
                                data.createdAt,
                                "dd/MM/yyyy 'às' HH:mm"
                              )
                            : undefined
                        }
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.updatedAt"
                        )}
                        value={
                          data?.updatedAt
                            ? DateHelper.format(
                                data.updatedAt,
                                "dd/MM/yyyy 'às' HH:mm"
                              )
                            : undefined
                        }
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.neverExpires"
                        )}
                        value={
                          data?.neverExpires
                            ? t("common.yes")
                            : t("common.no")
                        }
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.sendNotification"
                        )}
                        value={
                          data?.sendNotification
                            ? t("common.yes")
                            : t("common.no")
                        }
                      />
                      {data?.checkoutUrl && (
                        <div className="col-span-full min-w-0 overflow-hidden">
                          <InfoItem
                            label={t(
                              "subscriptions.details.fields.checkoutUrl"
                            )}
                            value={data.checkoutUrl}
                            copyable
                          />
                        </div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard
                    title={t(
                      "subscriptions.details.customerInfo"
                    )}
                    icon={<UserIcon size={16} />}
                    action={
                      data?.customer?.id && (
                        <Link
                          to={`/customers/details/${data.customer.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            buttonVariants({
                              variant: "ghost",
                              size: "sm",
                            }),
                            "h-7 gap-1 text-xs"
                          )}
                        >
                          <ExternalLinkIcon size={12} />
                          {t("common.viewDetails")}
                        </Link>
                      )
                    }
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <InfoItem
                        label={t("common.name")}
                        value={data?.customer?.fullName}
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.ownId"
                        )}
                        value={data?.ownId}
                        copyable
                      />
                      <InfoItem
                        label={t("common.email")}
                        value={data?.customer?.email}
                        copyable
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title={t(
                      "subscriptions.details.totalValue"
                    )}
                    icon={<ReceiptTextIcon size={16} />}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-center gap-2 sm:items-baseline sm:justify-start">
                        <span className="text-3xl font-bold text-primary">
                          {totalAmount}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "subscriptions.details.totalAmountNote"
                        )}
                      </p>
                      <Separator />
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.amount"
                          )}
                          value={
                            amount
                              ? formatMoney(amount / 100)
                              : "-"
                          }
                        />
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.discount"
                          )}
                          value={
                            discount
                              ? `-${formatMoney(discount / 100)}`
                              : formatMoney(0)
                          }
                        />
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.interest"
                          )}
                          value={
                            interest
                              ? formatMoney(interest / 100)
                              : formatMoney(0)
                          }
                        />
                      </div>
                    </div>
                  </SectionCard>

                  {hasPaymentMethods && (
                    <SectionCard
                      title={t(
                        "subscriptions.details.paymentMethods"
                      )}
                      icon={<CreditCardIcon size={16} />}
                      className="xl:col-span-3"
                    >
                      <div className="flex flex-wrap gap-2">
                        {data?.paymentMethods?.map(
                          (method, index) => (
                            <div
                              key={method.id || index}
                              className="rounded-md border bg-muted/50 px-3 py-2"
                            >
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <CreditCardIcon
                                    size={14}
                                    className="text-muted-foreground"
                                  />
                                  <span className="text-sm font-medium">
                                    {method.type}
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  <span>
                                    {t("common.status")}:{" "}
                                    {method.status}
                                  </span>
                                </div>
                                {method.createdAt && (
                                  <div className="text-xs text-muted-foreground">
                                    {DateHelper.format(
                                      method.createdAt,
                                      "dd/MM/yyyy HH:mm"
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </SectionCard>
                  )}

                  {hasPaymentConfig && (
                    <SectionCard
                      title={t(
                        "subscriptions.details.paymentConfiguration"
                      )}
                      icon={<SettingsIcon size={16} />}
                    >
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.daysDue"
                          )}
                          value={data?.defaultPaymentConfiguration?.daysDue?.toString()}
                        />
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.daysAfterDue"
                          )}
                          value={data?.defaultPaymentConfiguration?.daysAfterDue?.toString()}
                        />
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.preChargeDays"
                          )}
                          value={data?.defaultPaymentConfiguration?.preChargeDays?.toString()}
                        />
                        <InfoItem
                          label={t(
                            "subscriptions.details.fields.generateAllInvoices"
                          )}
                          value={
                            data
                              ?.defaultPaymentConfiguration
                              ?.generateAllInvoices
                              ? t("common.yes")
                              : t("common.no")
                          }
                        />
                        {data?.defaultPaymentConfiguration
                          ?.interest && (
                          <>
                            <InfoItem
                              label={t(
                                "subscriptions.details.fields.interestAmount"
                              )}
                              value={`${data.defaultPaymentConfiguration.interest.amount}%`}
                            />
                            <InfoItem
                              label={t(
                                "subscriptions.details.fields.interestType"
                              )}
                              value={
                                data
                                  .defaultPaymentConfiguration
                                  .interest.type
                              }
                            />
                          </>
                        )}
                        {data?.defaultPaymentConfiguration
                          ?.fine && (
                          <>
                            <InfoItem
                              label={t(
                                "subscriptions.details.fields.fineAmount"
                              )}
                              value={
                                data
                                  .defaultPaymentConfiguration
                                  .fine.type ===
                                "PERCENTAGE"
                                  ? `${data.defaultPaymentConfiguration.fine.amount}%`
                                  : formatMoney(
                                      data
                                        .defaultPaymentConfiguration
                                        .fine.amount / 100
                                    )
                              }
                            />
                            <InfoItem
                              label={t(
                                "subscriptions.details.fields.fineType"
                              )}
                              value={
                                data
                                  .defaultPaymentConfiguration
                                  .fine.type
                              }
                            />
                          </>
                        )}
                      </div>
                    </SectionCard>
                  )}

                  {hasMetadata && (
                    <SectionCard
                      title={t(
                        "subscriptions.details.metadata"
                      )}
                      icon={<SettingsIcon size={16} />}
                    >
                      <div className="space-y-4">
                        {data?.metadata?.map(
                          (item, index) => (
                            <div
                              key={`${item.key}-${index}`}
                              className="grid gap-4 rounded-md border bg-muted/30 p-3 sm:grid-cols-3"
                            >
                              <InfoItem
                                label={t(
                                  "subscriptions.details.fields.key"
                                )}
                                value={item.key}
                                copyable
                              />
                              <InfoItem
                                label={t(
                                  "subscriptions.details.fields.value"
                                )}
                                value={item.value}
                                copyable
                              />
                              <InfoItem
                                label={t(
                                  "subscriptions.details.fields.hideOnCheckout"
                                )}
                                value={
                                  item.hideOnCheckout
                                    ? t("common.yes")
                                    : t("common.no")
                                }
                              />
                            </div>
                          )
                        )}
                      </div>
                    </SectionCard>
                  )}
                </section>
              </TabsContent>
              <TabsContent
                value="cycles"
                className="space-y-6"
              >
                {dataTable && dataTable.length > 0 ? (
                  <section className="space-y-4">
                    <CyclesTable cycles={dataTable} />
                  </section>
                ) : (
                  <div className="flex items-center justify-center rounded-lg border border-dashed p-8">
                    <p className="text-sm text-muted-foreground">
                      {t("subscriptions.details.noCycles")}
                    </p>
                  </div>
                )}
              </TabsContent>
              <TabsContent
                value="insurance"
                className="space-y-6"
              >
                <section className="grid gap-4 lg:grid-cols-2">
                  <SectionCard
                    title={t(
                      "subscriptions.details.insuranceInfo"
                    )}
                    icon={<ShieldCheckIcon size={16} />}
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.policy"
                        )}
                        value={
                          data?.insurance?.policyNumber
                        }
                        copyable
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.type"
                        )}
                        value={
                          data?.insurance?.type?.name
                            ? getInsuranceTypeLabel(
                                data.insurance.type.name,
                                t
                              )
                            : undefined
                        }
                      />
                      <InfoItem
                        label={t(
                          "subscriptions.details.fields.createdAt"
                        )}
                        value={
                          data?.insurance?.createdAt
                            ? DateHelper.format(
                                data.insurance.createdAt,
                                "dd/MM/yyyy 'às' HH:mm"
                              )
                            : undefined
                        }
                      />
                      {hasCars && (
                        <div className="col-span-full">
                          <Separator className="my-3" />
                          <span className="mb-2 block text-xs font-medium text-muted-foreground">
                            {t(
                              "subscriptions.details.fields.vehicles"
                            )}
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {data?.insurance?.policyInformation?.cars?.map(
                              (car) => (
                                <div
                                  key={
                                    car.licensePlate ||
                                    car.model
                                  }
                                  className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2"
                                >
                                  <CarIcon
                                    size={14}
                                    className="text-muted-foreground"
                                  />
                                  <div className="text-sm">
                                    <span className="font-medium">
                                      {car.model}
                                    </span>
                                    <span className="mx-1 text-muted-foreground">
                                      •
                                    </span>
                                    <span className="font-mono text-xs">
                                      {car.licensePlate}
                                    </span>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </SectionCard>

                  {data?.insurance?.broker && (
                    <SectionCard
                      title={t(
                        "subscriptions.details.brokerInfo"
                      )}
                      icon={<BuildingIcon size={16} />}
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <InfoItem
                          label={t("common.name")}
                          value={data.insurance.broker.name}
                        />
                        <InfoItem
                          label={t("common.document")}
                          value={
                            data.insurance.broker.document
                          }
                          copyable
                        />
                        <InfoItem
                          label={t("common.email")}
                          value={
                            data.insurance.broker.email
                          }
                          copyable
                        />
                        {data.insurance.broker.phone && (
                          <InfoItem
                            label={t(
                              "subscriptions.details.fields.contact"
                            )}
                            value={`(${data.insurance.broker.phone.areaCode}) ${data.insurance.broker.phone.number}`}
                          />
                        )}
                      </div>
                    </SectionCard>
                  )}
                </section>
              </TabsContent>
            </Tabs>
          </>
        )}
      </CardContent>
      <CancelSubscriptionDialog
        id={data?.id as string}
        open={showCancelSubscriptionDialog}
        setOpen={setShowCancelSubscriptionDialog}
      />
    </Card>
  )
}
